import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import {
  EmploymentAdditionalProperty,
  EmploymentAdditionalPropertyType,
  EmploymentAdditionalPropertyValueIDType
} from 'generated';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions } from '@mui/material';

import {
  ControlledSelect,
  ControlledTextField,
  Drawer,
  showSnackbar
} from 'components/shared';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import ControlledValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';

import additionalPropertySchema from './additionalPropertySchema';

type Properties = {
  employmentId: number;
  isOpen: boolean;
  additionalProperty: EmploymentAdditionalProperty | null;
  handleClose: () => void;
};

const schema = additionalPropertySchema();
type Form = InferType<typeof schema>;

export default function AddEditAdditionalInformation(props: Properties) {
  const { employmentId, isOpen, additionalProperty, handleClose } = props;
  const { t } = useTranslation();

  const defaultValues = {
    validFrom: additionalProperty?.validFrom
      ? dayjs(additionalProperty.validFrom)
      : null,
    validTo: additionalProperty?.validTo
      ? dayjs(additionalProperty.validTo)
      : null,
    propertyType: additionalProperty?.propertyType,
    propertyValue: additionalProperty?.propertyValue?.value || '',
    propertyValueIDType: additionalProperty?.propertyValue?.propertyValueIDType,
    propertyValueAddressLine1:
      additionalProperty?.propertyValue?.addressLine1 || '',
    propertyValueAddressLine2:
      additionalProperty?.propertyValue?.addressLine2 || '',
    propertyValueAddressLine3:
      additionalProperty?.propertyValue?.addressLine3 || '',
    propertyValueAddressLine4:
      additionalProperty?.propertyValue?.addressLine4 || '',
    propertyValueAddressLine5:
      additionalProperty?.propertyValue?.addressLine5 || ''
  };

  const { control, handleSubmit, reset, watch } = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues
  });
  const propertyType = watch('propertyType');

  const queryClient = useQueryClient();
  const { mutate: createAdditionalProperty, isLoading: isCreateLoading } =
    mutations.useCreateEmploymentAdditionalProperty({
      onError: (e: any) => {
        showSnackbar({
          severity: 'error',
          message: t(e?.response?.data?.error?.message || 'failed')
        });
      },
      onSuccess: () => {
        handleClose();
        queryClient.invalidateQueries(
          queryKeys.getEmploymentAdditionalProperties(employmentId)
        );
        showSnackbar({
          severity: 'success',
          message: t('employmentAdditionalPropertySaveSuccess')
        });
      }
    });
  const { mutate: updateAdditionalProperty, isLoading: isUpdateLoading } =
    mutations.useUpdateEmploymentAdditionalProperty({
      onError: (e: any) => {
        showSnackbar({
          severity: 'error',
          message: t(e?.response?.data?.error?.message || 'failed')
        });
      },
      onSuccess: () => {
        handleClose();
        queryClient.invalidateQueries(
          queryKeys.getEmploymentAdditionalProperties(employmentId)
        );
        showSnackbar({
          severity: 'success',
          message: t('employmentAdditionalPropertySaveSuccess')
        });
      }
    });

  const onSubmit = async (data: Form) => {
    if (additionalProperty) {
      // update
      updateAdditionalProperty({
        employmentAdditionalPropertyId:
          additionalProperty.employmentAdditionalPropertyId,
        employmentId,
        propertyType: data.propertyType,
        propertyValue: {
          value: data.propertyValue || undefined,
          propertyValueIDType: data.propertyValueIDType || undefined,
          addressLine1: data.propertyValueAddressLine1 || undefined,
          addressLine2: data.propertyValueAddressLine2 || undefined,
          addressLine3: data.propertyValueAddressLine3 || undefined,
          addressLine4: data.propertyValueAddressLine4 || undefined,
          addressLine5: data.propertyValueAddressLine5 || undefined
        },
        validFrom: data.validFrom || undefined,
        validTo: data.validTo || undefined
      });
    } else {
      // create
      createAdditionalProperty({
        employmentId,
        propertyType: data.propertyType,
        propertyValue: {
          value: data.propertyValue || undefined,
          propertyValueIDType: data.propertyValueIDType || undefined,
          addressLine1: data.propertyValueAddressLine1 || undefined,
          addressLine2: data.propertyValueAddressLine2 || undefined,
          addressLine3: data.propertyValueAddressLine3 || undefined,
          addressLine4: data.propertyValueAddressLine4 || undefined,
          addressLine5: data.propertyValueAddressLine5 || undefined
        },
        validFrom: data.validFrom || undefined,
        validTo: data.validTo || undefined
      });
    }
  };

  const PROPERTY_TYPES: EmploymentAdditionalPropertyType[] = [
    'CHAMBER_OF_COMMERCE',
    'IDENTITY',
    'VAT_NUMBER',
    'DECLARATION_OF_CONDUCT',
    'COMPANY_DETAILS',
    'CHILDCARE_REGISTRATION',
    'BIG_REGISTRATION'
  ];

  const options: Option[] = PROPERTY_TYPES.map((item) => ({
    value: item,
    label: t(`employmentAdditionalPropertyTypes.${item}`)
  }));

  const ID_TYPES: EmploymentAdditionalPropertyValueIDType[] = [
    'PASSPORT',
    'IDENTITY_CARD',
    'DRIVER_LICENSE'
  ];

  const idOptions: Option[] = ID_TYPES.map((item) => ({
    value: item,
    label: t(`employmentAdditionalPropertyValueIDTypes.${item}`)
  }));

  const [showValueField, setShowValueField] = useState<boolean>(false);
  const [showIDTypeField, setShowIDTypeField] = useState<boolean>(false);
  const [showAddressFields, setShowAddressFields] = useState<boolean>(false);

  function updateFieldVisibility() {
    switch (propertyType) {
      case 'CHAMBER_OF_COMMERCE':
      case 'VAT_NUMBER':
      case 'DECLARATION_OF_CONDUCT':
      case 'CHILDCARE_REGISTRATION':
      case 'BIG_REGISTRATION':
        setShowValueField(true);
        setShowIDTypeField(false);
        setShowAddressFields(false);
        break;
      case 'IDENTITY':
        setShowValueField(true);
        setShowIDTypeField(true);
        setShowAddressFields(false);
        break;
      case 'COMPANY_DETAILS':
        setShowValueField(false);
        setShowIDTypeField(false);
        setShowAddressFields(true);
        break;
      default:
        setShowValueField(false);
        setShowIDTypeField(false);
        setShowAddressFields(false);
        break;
    }
  }

  useEffect(() => {
    reset(defaultValues);
  }, [additionalProperty]);
  useEffect(() => {
    reset(defaultValues);
    updateFieldVisibility();
  }, []);
  useEffect(() => {
    updateFieldVisibility();
  }, [propertyType]);

  return (
    <Drawer
      open={isOpen}
      title={t('employmentAdditionalProperty')}
      onClose={handleClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={2} mt={2} ml={2} mr={2}>
          <ControlledSelect
            control={control}
            label={t('employmentAdditionalPropertyType')}
            name="propertyType"
            options={options}
            disabled={!!additionalProperty}
          />
          {showIDTypeField && (
            <ControlledSelect
              control={control}
              label={t('employmentAdditionalPropertyValueIDType')}
              name="propertyValueIDType"
              options={idOptions}
            />
          )}
          {showValueField && (
            <ControlledTextField
              control={control}
              label={t('employmentAdditionalPropertyValue')}
              name="propertyValue"
            />
          )}
          {showAddressFields && (
            <>
              <ControlledTextField
                control={control}
                label={t('employmentAdditionalPropertyValueAddressLine1')}
                name="propertyValueAddressLine1"
              />
              <ControlledTextField
                control={control}
                label={t('employmentAdditionalPropertyValueAddressLine2')}
                name="propertyValueAddressLine2"
              />
              <ControlledTextField
                control={control}
                label={t('employmentAdditionalPropertyValueAddressLine3')}
                name="propertyValueAddressLine3"
              />
              <ControlledTextField
                control={control}
                label={t('employmentAdditionalPropertyValueAddressLine4')}
                name="propertyValueAddressLine4"
              />
              <ControlledTextField
                control={control}
                label={t('employmentAdditionalPropertyValueAddressLine5')}
                name="propertyValueAddressLine5"
              />
            </>
          )}
          <ControlledValidFromValidTo control={control} />
        </Box>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <LoadingButton
            loading={isCreateLoading || isUpdateLoading}
            type="submit"
            color="primary"
            variant="contained"
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Drawer>
  );
}
